import { supabase } from '@/integrations/supabase/client';
import { ApiService } from "@/services/api/ApiService";
import { CONNECTION_LOST_ACTION } from "@/services/integrations/elevenlabs/connectionLossLog";

import type {
  ConversationLog,
  ConversationLogDetail,
  ConversationLogFilters,
  ConversationLogStats,
  ConversationLogsResponse,
  ConversationLogDetailsResponse,
} from "@/types/conversation-logs";

export class ConversationLogsService {
  static async getConversationLogs(
    filters?: ConversationLogFilters,
    pageSize: number = 50,
    offset: number = 0,
  ): Promise<ConversationLogsResponse> {
    try {
      let query = supabase
        .from("conversation_logs")
        .select(
          `*,
           conversations!inner(id, type, status, metadata),
           agents!conversation_logs_agent_id_fkey(logo_url:branding->>logoUrl, custom_avatar_url:branding->>customAvatarUrl)${
             filters?.connection_lost ? ", conversation_log_details!inner(action)" : ""
           }`,
          { count: "exact" },
        )
        .order("created_at", { ascending: false });

      if (filters?.connection_lost) {
        query = query.eq("conversation_log_details.action", CONNECTION_LOST_ACTION);
      }

      if (filters?.search) {
        query = query.or(
          `agent_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,el_conv_id.ilike.%${filters.search}%`,
        );
      }

      if (filters?.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }

      if (filters?.agent_id) {
        query = query.eq("agent_id", filters.agent_id);
      }

      if (filters?.conversation_type && filters.conversation_type !== "all") {
        query = query.eq("conversations.type", filters.conversation_type);
      }

      const { data, count, error } = await query.range(offset, offset + pageSize - 1);

      if (error) {
        throw new Error(`Failed to fetch conversation logs: ${error.message}`);
      }

      const mappedData = (data || []).map((log: any) => ({
        ...log,
        conversation_type: log.conversations?.type || null,
        conversation_status: log.conversations?.status || null,
        created_agent_id: log.conversations?.metadata?.createdAgentId || null,
        agent_logo_url: log.agents?.logo_url || log.agents?.custom_avatar_url || null,
      }));

      return {
        data: mappedData,
        count: count || 0,
      };
    } catch (error: any) {
      console.error("Error fetching conversation logs:", error);
      throw error;
    }
  }

  static async getConversationLog(id: number): Promise<ConversationLog> {
    try {
      const { data, error } = await supabase.from("conversation_logs").select("*").eq("id", id).single();

      if (error) {
        throw new Error(`Failed to fetch conversation log: ${error.message}`);
      }

      return data;
    } catch (error: any) {
      console.error("Error fetching conversation log:", error);
      throw error;
    }
  }

  static async getConversationLogDetails(logId: number): Promise<ConversationLogDetailsResponse> {
    try {
      const { data, error } = await supabase
        .from("conversation_log_details")
        .select("*")
        .eq("log_id", logId)
        .order("created_at", { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch conversation log details: ${error.message}`);
      }

      return {
        data: data || [],
      };
    } catch (error: any) {
      console.error("Error fetching conversation log details:", error);
      throw error;
    }
  }

  static async getConversationLogStats(filters?: ConversationLogFilters): Promise<ConversationLogStats> {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    return ApiService.invoke<ConversationLogStats>({
      resource: "conversation-logs",
      action: "stats",
      data: { ...filters, today_start: todayStart.toISOString() },
    });
  }

  static async resolveConversationAuthorUserId(conversationLog: any): Promise<string | null> {
    try {
      // 1. Try using user_id from conversation_logs
      if (conversationLog.user_id) {
        return conversationLog.user_id;
      }

      // 2. Try resolving from profiles table using email
      if (conversationLog.email) {
        const { data, error } = await supabase.from("profiles").select("id").eq("email", conversationLog.email).single();

        if (!error && data?.id) {
          return data.id;
        }
      }

      // 3. Return null if no user found, will default to current authenticated user
      return null;
    } catch (error: any) {
      console.error("Error resolving conversation author user_id:", error);
      return null;
    }
  }

  static async exportToCSV(filters?: ConversationLogFilters): Promise<string> {
    try {
      let query = supabase.from("conversation_logs").select(
        `*,
         conversations!inner(id, type, status)${filters?.connection_lost ? ", conversation_log_details!inner(action)" : ""}`,
      );

      if (filters?.connection_lost) {
        query = query.eq("conversation_log_details.action", CONNECTION_LOST_ACTION);
      }

      if (filters?.search) {
        query = query.or(
          `agent_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,el_conv_id.ilike.%${filters.search}%`,
        );
      }

      if (filters?.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }

      if (filters?.agent_id) {
        query = query.eq("agent_id", filters.agent_id);
      }

      if (filters?.conversation_type && filters.conversation_type !== "all") {
        query = query.eq("conversations.type", filters.conversation_type);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to export logs: ${error.message}`);
      }

      if (!data || data.length === 0) {
        return "agent_name,email,status,conversation_type,created_at,el_conv_id\n";
      }

      const headers = ["agent_name", "email", "status", "conversation_type", "created_at", "el_conv_id"];
      const rows = data.map((logWithConv: any) => {
        const log = {
          ...logWithConv,
          conversation_type: logWithConv.conversations?.type || null,
        };
        return headers
          .map((header) => {
            const value = log[header as keyof any];
            const stringValue = String(value || "");
            return `"${stringValue.replace(/"/g, '""')}"`;
          })
          .join(",");
      });

      return `${headers.join(",")}\n${rows.join("\n")}`;
    } catch (error: any) {
      console.error("Error exporting conversation logs:", error);
      throw error;
    }
  }
}
